import { useEffect, useState, type ReactNode } from "react";
import { MdArrowOutward } from "react-icons/md";
import MeritoButton from "../../../../../components/common/MeritoButtons/MeritoButtons";
import CopyLinkButton from "../../common/CopyLinkButton/CopyLinkButton";
import ProfileConfirmPanel from "../../common/ProfileConfirmPanel/ProfileConfirmPanel";
import styles from "./PhaseActions.module.scss";

/**
 * Opening the provider's settings is three steps, not one button: name the step, confirm which
 * mailbox is about to open, then ask whether it was done. Asking "did you set it up?" beside the
 * button that opens the settings puts the question before the action.
 */
type PhaseStep = "start" | "confirmProfile" | "confirmDone";

export interface PhaseActionsProps {
  providerName: string;
  accountEmail: string;
  settingsUrl: string | null;
  settingsLabel: string;
  confirmLabel: string;
  declineLabel: string;
  /** A second line on the confirm-profile step, where the provider needs one. */
  confirmNote?: ReactNode;
  /** Replaces the confirm/decline pair on the last step — Outlook puts its rule cards here. */
  confirmSlot?: ReactNode;
  canOpen?: boolean;
  canCopy?: boolean;
  canConfirm?: boolean;
  disabled?: boolean;
  busy?: boolean;
  /** Changing this sends the panel back to the first step — a re-entered card never resumes midway. */
  resetKey?: string;
  /**
   * The delegate page's Figma look: the step's main action is a dark rounded primary and the
   * companion a plain text button. The hub modal keeps the default bordered style.
   */
  appearance?: "default" | "primary";
  /** Renders the confirm-profile step as a grey panel with an × that steps back. */
  confirmDismissible?: boolean;
  /** The filter phase offers "I've set it up" before the settings were ever opened. */
  confirmBeforeOpen?: boolean;
  /** "Yes, Continue" style; follows `appearance` unless set (Outlook's delegate frame keeps it bordered). */
  continueAppearance?: "primary" | "secondary";
  /**
   * Rendered on the left of the button row (the delegate page's Back), below the confirm panel on
   * the confirm-profile step, and after `confirmSlot` when that replaces the buttons.
   */
  leading?: ReactNode;
  onOpen: () => void;
  onConfirm: () => void;
}

const PhaseActions = ({
  providerName,
  accountEmail,
  settingsUrl,
  settingsLabel,
  confirmLabel,
  declineLabel,
  confirmNote,
  confirmSlot,
  canOpen = true,
  canCopy = false,
  canConfirm = true,
  disabled = false,
  busy = false,
  resetKey,
  appearance = "default",
  confirmDismissible = false,
  confirmBeforeOpen = false,
  continueAppearance,
  leading,
  onOpen,
  onConfirm,
}: PhaseActionsProps): JSX.Element => {
  const [step, setStep] = useState<PhaseStep>("start");

  useEffect(() => { setStep("start"); }, [resetKey]);

  const primary = appearance === "primary";
  const mainType = primary ? "primary-1" : "secondary-2";
  const mainExtras = primary ? { isRounded: true } : {};
  const textType = primary ? "tertiary" : "secondary-2";
  const continuePrimary = (continueAppearance ?? (primary ? "primary" : "secondary")) === "primary";

  // With a leading node the buttons sit on its right, on one row (Figma: Back left, actions right).
  const row = (buttons: ReactNode): JSX.Element => (leading ? (
    <div className={styles.leadingRow}>
      {leading}
      <div className={styles.actions}>{buttons}</div>
    </div>
  ) : <div className={styles.actions}>{buttons}</div>);

  // The link the Open button navigates to is the link this copies — one source, so they cannot diverge.
  const copyButton = canCopy ? (
    <CopyLinkButton value={settingsUrl} disabled={disabled} className={styles.action} />
  ) : null;

  const textConfirmButton = confirmBeforeOpen && canConfirm ? (
    <MeritoButton
      type="tertiary"
      size="md"
      className={styles.action}
      btnText={confirmLabel}
      loading={busy}
      showLoadingWithText
      disabled={disabled}
      onClick={onConfirm}
    />
  ) : null;

  if (step === "confirmDone" && confirmSlot) {
    return (
      <>
        {confirmSlot}
        {leading ? <div className={styles.leadingRow}>{leading}</div> : null}
      </>
    );
  }

  if (step === "confirmDone" && canConfirm) {
    return row(
      <>
        <MeritoButton
          type={mainType}
          size="md"
          className={styles.action}
          btnText={confirmLabel}
          loading={busy}
          showLoadingWithText
          disabled={disabled}
          {...mainExtras}
          onClick={onConfirm}
        />
        {confirmBeforeOpen && canOpen ? (
          <MeritoButton
            type={textType}
            size="md"
            className={styles.action}
            btnText={settingsLabel}
            disabled={!settingsUrl || disabled || busy}
            onClick={() => setStep("confirmProfile")}
          />
        ) : null}
        {declineLabel ? (
          <MeritoButton
            type="tertiary"
            size="md"
            className={styles.action}
            btnText={declineLabel}
            disabled={disabled || busy}
            onClick={() => setStep("start")}
          />
        ) : null}
      </>
    );
  }

  if (step === "confirmProfile") {
    return (
      <>
        <ProfileConfirmPanel
          providerName={providerName}
          accountEmail={accountEmail}
          note={confirmNote}
          {...(confirmDismissible ? { onDismiss: () => setStep("start") } : {})}
        >
          <MeritoButton
            type={continuePrimary ? "primary-1" : "secondary-2"}
            size="md"
            className={styles.action}
            btnText="Yes, Continue"
            icon={<MdArrowOutward aria-hidden="true" />}
            isIconToRight
            disabled={!settingsUrl || disabled}
            {...(continuePrimary && primary ? { isRounded: true } : {})}
            onClick={() => {
              onOpen();
              // Only asks whether it was done once the settings have actually been opened.
              if (canConfirm || confirmSlot) setStep("confirmDone");
            }}
          />
          {copyButton}
        </ProfileConfirmPanel>
        {textConfirmButton ? <div className={styles.trailing}>{textConfirmButton}</div> : null}
        {leading ? <div className={styles.leadingRow}>{leading}</div> : null}
      </>
    );
  }

  const openButton = canOpen ? (
    <MeritoButton
      type={mainType}
      size="md"
      className={styles.action}
      btnText={settingsLabel}
      disabled={!settingsUrl || disabled}
      {...mainExtras}
      onClick={() => setStep("confirmProfile")}
    />
  ) : null;

  // Beside a leading Back, Figma reads right-to-left: Copy Link, then the primary Open on the edge.
  return row(leading ? (
    <>
      {textConfirmButton}
      {copyButton}
      {openButton}
    </>
  ) : (
    <>
      {openButton}
      {textConfirmButton}
      {copyButton}
    </>
  ));
};

export default PhaseActions;
